import BellRingersSvg from '../assets/icons/BellRingersSvg';
import BookSvg from '../assets/icons/BookSvg';
import BrainbreaksSvg from '../assets/icons/BrainbreaksSvg';
import DebateSvg from '../assets/icons/DebateSvg';
import InformationalWritingSvg from '../assets/icons/InformationalWritingSvg';
import MathSvg from '../assets/icons/MathSvg';
import QuickSvg from '../assets/icons/QuickSvg';
import RocketSvg from '../assets/icons/RocketSvg';
import ScienceSvg from '../assets/icons/ScienceSvg';
import SELSvg from '../assets/icons/SELSvg';
import SocialStudiesSvg from '../assets/icons/SocialStudiesSvg';
import AmericaSvg from '../assets/icons/StateIcons/AmericaSvg';
import ArizonaSvg from '../assets/icons/StateIcons/ArizonaSvg';
import CaliforniaSvg from '../assets/icons/StateIcons/CaliforniaSvg';
import ColoradoSvg from '../assets/icons/StateIcons/ColoradoSvg';
import GeorgiaSvg from '../assets/icons/StateIcons/GeorgiaSvg';
import MSTEPSvg from '../assets/icons/StateIcons/MSTEPSvg';
import NCSvg from '../assets/icons/StateIcons/NCSvg';
import NYSvg from '../assets/icons/StateIcons/NYSvg';
import OhioSvg from '../assets/icons/StateIcons/OhioSvg';
import PASvg from '../assets/icons/StateIcons/PASvg';
import TelpasSvg from '../assets/icons/StateIcons/TelpasSvg';
import TexasSvg from '../assets/icons/StateIcons/TexasSvg';
import WestVirginiaSvg from '../assets/icons/StateIcons/WestVirginiaSvg';
import VocabSvg from '../assets/icons/VocabSvg';
import fullLessonImg from '../assets/images/card-category-full_lesson.png';
import literacyImg from '../assets/images/card-category-literacy.png';
import routinesImg from '../assets/images/card-category-routines.png';
import testingImg from '../assets/images/card-category-testing.png';
import uploadImg from '../assets/images/card-category-upload.png';
import theme from '../assets/theme';
import { ById } from '../services/backendService/types';
import { notUndefined } from './general';
import { allLanguages } from './language-list';

type SubCategoryType =
  | 'generators'
  | 'lessons'
  | 'results'
  | 'recent_generators'
  | 'latest_generators'
  | 'custom_generators';

type ContentSubCategory = {
  id: string;
  title: string;
  type: SubCategoryType;
};

export type ContentCategory = {
  id: string;
  title: string;
  icon: React.ReactElement;
  subCategories: ContentSubCategory[];
};

export type ContentHierarchy = DiscoveryContentMapper[];

type DiscoveryContentMapper = {
  subject: string;
  subjectDisplayName: string;
  categories: ContentCategory[];
};

export const subjects: string[] = [
  'ELA',
  'Social studies',
  'Science',
  'Math',
  'World languages',
  'Mental health',
  'Other',
];

export const subjectOptions: {
  value: string;
  displayName: string;
}[] = [
  { value: 'ela', displayName: 'English Language Arts' },
  { value: 'social_studies', displayName: 'Social Studies' },
  { value: 'science', displayName: 'Science' },
  { value: 'math', displayName: 'Math' },
  { value: 'world_languages', displayName: 'World Languages' },
  { value: 'mental_health', displayName: 'Mental Health' },
  { value: 'other', displayName: 'Other' },
];

export type LessonCategory = {
  title: string;
  description: string;
  categories?: ContentCategory[];
  img: string;
  color: string;
};

export const writingSkills: LessonCategory = {
  title: 'Build writing skills',
  description: 'Learn and practice core reading and writing skills',
  img: literacyImg.src,
  color: theme.colors.orange,
  categories: [
    {
      id: 'five_min_writing',
      title: '5 Min Writing Practice',
      icon: <QuickSvg />,
      subCategories: [],
    },
    {
      id: 'argumentative_writing',
      title: 'Argumentative writing',
      icon: <DebateSvg />,
      subCategories: [],
    },
    {
      id: 'informational_writing',
      title: 'Informational writing',
      icon: <InformationalWritingSvg />,
      subCategories: [],
    },
    {
      id: 'narrative_writing',
      title: 'Narrative writing',
      icon: <BookSvg />,
      subCategories: [],
    },
  ],
};

export const lessonCategories: ById<LessonCategory> = {
  upload: {
    title: 'Curify my slides',
    description:
      'Upload your slides and enhance with interactive activities and AI feedback',
    img: uploadImg.src,
    color: theme.colors.red,
  },
  full_lesson: {
    title: 'Generate a lesson',
    description: 'Aligned to skills, content and standards',
    img: fullLessonImg.src,
    color: theme.colors.green,
  },
  test_prep: {
    title: 'Prepare for ',
    description: 'Prepare your students for your standardized tests',
    color: theme.colors.blue,
    img: testingImg.src,
    categories: [
      {
        id: 'college_entrance_exams',
        title: 'ACT & AP',
        icon: <AmericaSvg />,
        subCategories: [],
      },
      {
        id: 'sbac',
        title: 'SBAC',
        icon: <AmericaSvg />,
        subCategories: [],
      },
      {
        id: 'staar',
        title: 'STAAR',
        icon: <TexasSvg />,
        subCategories: [],
      },
      {
        id: 'california_sbac_caaspp',
        title: 'CAASPP',
        icon: <CaliforniaSvg />,
        subCategories: [],
      },
      {
        id: 'georgia_gmas',
        title: 'GMAS',
        icon: <GeorgiaSvg />,
        subCategories: [],
      },
      {
        id: 'telpas',
        title: 'TELPAS',
        icon: <TelpasSvg />,
        subCategories: [],
      },
      {
        id: 'mstep',
        title: 'M-STEP',
        icon: <MSTEPSvg />,
        subCategories: [],
      },
      {
        id: 'wida',
        title: 'WIDA',
        icon: <AmericaSvg />,
        subCategories: [],
      },
      {
        id: 'ny_stp',
        title: 'NYS Exam Prep',
        icon: <NYSvg />,
        subCategories: [],
      },
      {
        id: 'colorado_cmas',
        title: 'CMAS',
        icon: <ColoradoSvg />,
        subCategories: [],
      },
      {
        id: 'arizona_aasa',
        title: 'AASA',
        icon: <ArizonaSvg />,
        subCategories: [],
      },
      {
        id: 'ohio_ost',
        title: 'OST',
        icon: <OhioSvg />,
        subCategories: [],
      },
      {
        id: 'ohio_oelpa',
        title: 'OELPA',
        icon: <OhioSvg />,
        subCategories: [],
      },
      {
        id: 'west_virginia_wvgsa',
        title: 'WVGSA',
        icon: <WestVirginiaSvg />,
        subCategories: [],
      },
      {
        id: 'westvirginia_elpa21',
        title: 'ELPA21',
        icon: <WestVirginiaSvg />,
        subCategories: [],
      },
      {
        id: 'north_carolina_test_prep',
        title: 'NC Test Prep',
        icon: <NCSvg />,
        subCategories: [],
      },
      {
        id: 'pennsylvania_pssa',
        title: 'PSSA',
        icon: <PASvg />,
        subCategories: [],
      },
    ],
  },
};

export const mobileOrderLessonCategories = [
  'full_lesson',
  'upload',
  'test_prep',
  'writing_skills',
  'daily_routines',
];

export const fullLessonExamplesELA: ExamplePrompt[] = [
  {
    title: 'To Kill a Mockingbird chapter 3 novel study. Analyze characterization.',
    icon: <BookSvg />,
    topicInput: 'To Kill a Mockingbird chapter 3 novel study. Analyze characterization.',
    customizationInput: `Focus on Scout Finch's traits.`,
  },
  {
    title: 'Effective topic sentences with a practice prompt about animals',
    icon: <BookSvg />,
    topicInput: 'Effective topic sentences with a practice prompt about animals.',
    customizationInput: `Make it 10 slides`,
  },
  {
    title:
      'Discussion questions about unreliable narrators for The Tell-Tale Heart by Edgar Allan Poe.',
    icon: <BookSvg />,
    topicInput:
      'Discussion questions about unreliable narrators for The Tell-Tale Heart by Edgar Allan Poe.',
    customizationInput: undefined,
  },
  {
    title: 'Action verbs, with real-life examples specific to professional sports.',
    icon: <BookSvg />,
    topicInput: 'Action verbs, with real-life examples specific to professional sports. ',
    customizationInput: 'Make sure the sport referenced is soccer.',
  },
];

export const fullLessonExamplesMath: ExamplePrompt[] = [
  {
    title:
      'Negative numbers (integers), make it about winning and losing points in a game.',
    icon: <MathSvg />,
    topicInput:
      'Negative numbers (integers), make it about winning and losing points in a game.',
    customizationInput: undefined,
  },
  {
    title:
      'Word problems to practice using benchmarks to estimate. Use the names Erik and Ava.',
    icon: <MathSvg />,
    topicInput:
      'Word problems to practice using benchmarks to estimate. Use the names Erik and Ava.',
    customizationInput: undefined,
  },
  {
    title: 'Probability of independent compound events.',
    icon: <MathSvg />,
    topicInput: 'How to calculate probability of independent compound events.',
    customizationInput: undefined,
  },
  {
    title: 'Practice factoring polynomials.',
    icon: <MathSvg />,
    topicInput: 'Practice factoring polynomials.',
    customizationInput: undefined,
  },
];

export const fullLessonExamplesScience: ExamplePrompt[] = [
  {
    title: `Animal adaptations. Include surprising examples from different ecosystems.`,
    icon: <ScienceSvg />,
    topicInput: `Animal adaptations. Include surprising examples from different ecosystems.`,
    customizationInput: undefined,
  },
  {
    title:
      'Introduce cellular respiration with questions that require students to apply their learning.',
    icon: <ScienceSvg />,
    topicInput: `Introduce cellular respiration with questions that require students to apply their learning.`,
    customizationInput: undefined,
  },
  {
    title:
      'Explore the water cycle. Define evaporation, condensation, and precipitation with real-life examples.',
    icon: <ScienceSvg />,
    topicInput:
      'Explore the water cycle. Define evaporation, condensation, and precipitation with real-life examples.',
    customizationInput: undefined,
  },
  {
    title: 'Introduce potential and kinetic energy, include examples from sports.',
    icon: <ScienceSvg />,
    topicInput: 'Introduce potential and kinetic energy, include examples from sports.',
    customizationInput: undefined,
  },
];

export const fullLessonExamplesSocialStudies: ExamplePrompt[] = [
  {
    title:
      'The invention of money in ancient civilizations and the impact on trade and society.',
    icon: <SocialStudiesSvg />,
    topicInput:
      'The invention of money in ancient civilizations and the impact on trade and society.',
    customizationInput: undefined, //TODO do we need this??
  },
  {
    title: 'The Louisiana Purchase. Include AI feedback from Thomas Jefferson.',
    icon: <SocialStudiesSvg />,
    topicInput: 'The Louisiana Purchase. Include AI feedback from Thomas Jefferson.',
    customizationInput: undefined,
  },
  {
    title: 'Compare and contrast U.S. and Soviet Union priorities during the Cold War.',
    icon: <SocialStudiesSvg />,
    topicInput:
      'Compare and contrast U.S. and Soviet Union priorities during the Cold War.',
    customizationInput: undefined,
  },
  {
    title:
      'Tell about 3 prominent historical figures in the Renaissance and their modern-day impact.',
    icon: <SocialStudiesSvg />,
    topicInput:
      'Tell about 3 prominent historical figures in the Renaissance and their modern-day impact.',
    customizationInput: undefined,
  },
];

const fullLessonExamplesWorldLanguages: ExamplePrompt[] = [
  {
    title:
      'Compare Spanish winter holiday traditions with U.S. winter holiday traditions.',
    icon: <BookSvg />,
    topicInput:
      'Compare Spanish winter holiday traditions with U.S. winter holiday traditions.',
    customizationInput: undefined,
  },
  {
    title: 'Introduce the imperfect tense in French.',
    icon: <BookSvg />,
    topicInput:
      'Introduce the imperfect tense in French. Include practice prompts about everyday life.',
    customizationInput: undefined,
  },
  {
    title: 'Engaging prompts about eating to practice speaking in German. ',
    icon: <BookSvg />,
    topicInput: 'Engaging prompts about eating to practice speaking in German. ',
    customizationInput: undefined,
  },
  {
    title: 'Practice translating Latin sentences about the Olympics .',
    icon: <BookSvg />,
    topicInput: 'Practice translating Latin sentences about the Olympics.',
    customizationInput: undefined,
  },
];

export const getRandomExamples = (subject?: string) => {
  if (subject === 'ela') {
    return fullLessonExamplesELA;
  }
  if (subject === 'math') {
    return fullLessonExamplesMath;
  }
  if (subject === 'science') {
    return fullLessonExamplesScience;
  }
  if (subject === 'socialStudies') {
    return fullLessonExamplesSocialStudies;
  }

  if (subject === 'worldLanguages') {
    return fullLessonExamplesWorldLanguages;
  }
  //if no subject given then create an array of 4 random examples one from each subject
  const randomExamples = [
    fullLessonExamplesELA[Math.floor(Math.random() * fullLessonExamplesELA.length)],
    fullLessonExamplesSocialStudies[
      Math.floor(Math.random() * fullLessonExamplesSocialStudies.length)
    ],
    fullLessonExamplesScience[
      Math.floor(Math.random() * fullLessonExamplesScience.length)
    ],
    fullLessonExamplesMath[Math.floor(Math.random() * fullLessonExamplesMath.length)],
  ];
  return randomExamples;
};

export type ExamplePrompt = {
  title: string;
  topicInput: string;
  icon: React.ReactElement;
  customizationInput?: string;
};

export const supportedLanguages: {
  displayName: string;
  value: string;
}[] = [
  { value: 'english', displayName: 'English (US)' },
  { value: 'spanish', displayName: 'Spanish' },
  {
    value: 'australian english',
    displayName: 'English (Australia)',
  },
  { value: 'english UK', displayName: 'English (UK)' },
  { value: 'norwegian', displayName: 'Norwegian' },
  { value: 'swedish', displayName: 'Swedish' },
  { value: 'german', displayName: 'German' },
  { value: 'french', displayName: 'French' },
  ...Object.values(allLanguages).map(lang => {
    //filter out those we want to place at the top
    if (
      ['English', 'Spanish', 'French', 'German', 'Swedish', 'Norwegian'].includes(
        lang.int,
      )
    ) {
      return undefined;
    }
    return {
      value: lang.int,
      displayName: lang.int,
    };
  }),
].filter(notUndefined);

export type SubjectOption = {
  displayName: string;
  id: lessonIntents;
  comingSoon?: boolean;
  hasMoreDetails?: boolean;
  prompt?: string;
  dropdownOptions?: {
    value: string;
    displayName: string;
    displayDivider?: boolean;
    subCategory?: string;
  }[];
};

export type lessonIntents =
  | 'novelStudy'
  | 'readingStrategies'
  | 'writingSkills'
  | 'quickWrites'
  | 'shortResponse'
  | 'partsOfAnEssay'
  | 'history'
  | 'civicsGovernment'
  | 'economics'
  | 'psychology'
  | 'earthScience'
  | 'spaceScience'
  | 'biology'
  | 'chemistry'
  | 'physics'
  | 'engineering'
  | 'numberSense'
  | 'measurement'
  | 'algebra'
  | 'geometry'
  | 'statistics'
  | 'trigonometry'
  | 'calculus'
  //V2 lesson types
  | 'teachSomethingNew'
  | 'reviewOrReteach'
  | 'practiceASkill'
  | 'readAloud'
  | 'closeReading'
  | 'other';

type GenerateWithAIType = {
  [key: string]: {
    displayName: string;
    examplePrompts: ExamplePrompt[] | undefined;
    lessonIntents: SubjectOption[] | undefined;
  };
};

export const subjectOptionMapper: GenerateWithAIType = {
  ela: {
    displayName: 'ELA',
    examplePrompts: fullLessonExamplesELA,
    lessonIntents: [
      {
        displayName: 'Novel Study',
        id: 'novelStudy',
        hasMoreDetails: true,
        prompt: undefined,
        dropdownOptions: [
          { value: 'authorsPurpose', displayName: "Author's Purpose" },
          { value: 'characterization', displayName: 'Characterization' },
          { value: 'figurativeLanguage', displayName: 'Figurative Language' },
          { value: 'flashback', displayName: 'Flashback' },
          { value: 'foreshadowing', displayName: 'Foreshadowing' },
          { value: 'imagery', displayName: 'Imagery' },
          { value: 'inferencing', displayName: 'Inferencing' },
          { value: 'irony', displayName: 'Irony' },
          { value: 'mainIdeas', displayName: 'Main Ideas' },
          { value: 'mood', displayName: 'Mood' },
          { value: 'plotElements', displayName: 'Plot Elements' },
          { value: 'pointOfView', displayName: 'Point of View' },
          { value: 'predictions', displayName: 'Predictions' },
          { value: 'setting', displayName: 'Setting' },
          { value: 'symbolism', displayName: 'Symbolism' },
          { value: 'theme', displayName: 'Theme' },
          { value: 'tone', displayName: 'Tone' },
          { value: 'typesOfNarrators', displayName: 'Types of Narrators' },
          {
            value: 'other',
            displayName: 'Other (Add your own focus)',
            displayDivider: true,
          },
        ],
      },
      {
        displayName: 'Close Reading',
        id: 'closeReading',
        prompt: undefined,
        comingSoon: true,
      },
      {
        displayName: 'Reading Strategies',
        id: 'readingStrategies',
        comingSoon: false,
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to use {{focusName}} in reading comprehension and analysis.',
        dropdownOptions: [
          { value: 'mainIdea', displayName: 'Main Idea' },
          { value: 'centralMessage', displayName: 'Central Message' },
          { value: 'theme', displayName: 'Theme' },
          { value: 'motifs', displayName: 'Motifs' },
          { value: 'summarizing', displayName: 'Summarizing' },
          { value: 'paraphrasing', displayName: 'Paraphrasing' },
          { value: 'inferencing', displayName: 'Inferencing' },
          { value: 'makingConnections', displayName: 'Making Connections' },
          { value: 'visualizing', displayName: 'Visualizing' },
          { value: 'questioning', displayName: 'Questioning' },
          { value: 'predicting', displayName: 'Predicting' },
          { value: 'comprehensionMonitoring', displayName: 'Comprehension Monitoring' },
          { value: 'authorsPurpose', displayName: "Author's Purpose" },
          { value: 'previewing', displayName: 'Previewing' },
          { value: 'textStructure', displayName: 'Text Structure' },
          { value: 'literaryElements', displayName: 'Literary Elements' },
          { value: 'rhetoricalDevices', displayName: 'Rhetorical Devices' },
          {
            value: 'other',
            displayName: 'Other (Add your own focus)',
            displayDivider: true,
          },
        ],
      },
      {
        displayName: 'Writing Skills',
        id: 'writingSkills',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will have learned about {{focusName}} as a writing skill.',
        dropdownOptions: [
          { value: 'activeAndPassiveVoice', displayName: 'Active and Passive Voice' },
          { value: 'actionVerbs', displayName: 'Action Verbs' },
          { value: 'alliteration', displayName: 'Alliteration' },
          { value: 'appositives', displayName: 'Appositives' },
          { value: 'characterization', displayName: 'Characterization' },
          { value: 'conjunctions', displayName: 'Conjunctions' },
          { value: 'dialogue', displayName: 'Dialogue' },
          { value: 'figurativeLanguage', displayName: 'Figurative Language' },
          {
            value: 'formalAndInformalLanguage',
            displayName: 'Formal and Informal Language',
          },
          { value: 'hyperbole', displayName: 'Hyperbole' },
          { value: 'imagery', displayName: 'Imagery' },
          { value: 'metaphors', displayName: 'Metaphors' },
          { value: 'parallelStructure', displayName: 'Parallel Structure' },
          { value: 'paraphrasing', displayName: 'Paraphrasing' },
          { value: 'personification', displayName: 'Personification' },
          { value: 'prepositions', displayName: 'Prepositions' },
          { value: 'similes', displayName: 'Similes' },
          { value: 'symbolism', displayName: 'Symbolism' },
          { value: 'transitionWords', displayName: 'Transition Words' },
          {
            value: 'other',
            displayName: 'Other (Add your own focus)',
            displayDivider: true,
          },
        ],
      },
      {
        displayName: 'Short Response',
        id: 'shortResponse',
        comingSoon: false,
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to use {{focusName}} in writing a short response.',
        dropdownOptions: [
          { value: 'topicSentence', displayName: 'Topic Sentence' },
          { value: 'claim', displayName: 'Claim' },
          { value: 'evidence', displayName: 'Evidence' },
          { value: 'citingEvidence', displayName: 'Citing Evidence from a Text' },
          { value: 'supportingDetails', displayName: 'Supporting Details' },
          { value: 'explainingEvidence', displayName: 'Explaining Evidence' },
          { value: 'transitionWords', displayName: 'Transition Words' },
          {
            value: 'other',
            displayName: 'Other (Add your own focus)',
            displayDivider: true,
          },
        ],
      },
      {
        displayName: 'Parts of an Essay',
        id: 'partsOfAnEssay',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to use {{focusName}} in essay writing.',
        dropdownOptions: [
          {
            value: 'causeEffectTextStructure',
            displayName: 'Cause/Effect Text Structure',
          },
          { value: 'choosingEvidence', displayName: 'Choosing Evidence' },
          {
            value: 'choosingSupportingDetails',
            displayName: 'Choosing Supporting Details',
          },
          { value: 'claims', displayName: 'Claims' },
          {
            value: 'compareContrastTextStructure',
            displayName: 'Compare/Contrast Text Structure',
          },
          { value: 'conclusions', displayName: 'Conclusions' },
          { value: 'counterarguments', displayName: 'Counterarguments' },
          {
            value: 'credibilityAndAuthority',
            displayName: 'Credibility and Authority (Ethos)',
          },
          {
            value: 'descriptiveTextStructure',
            displayName: 'Descriptive Text Structure',
          },
          { value: 'emotionalAppeal', displayName: 'Emotional Appeal (Pathos)' },
          { value: 'explainingEvidence', displayName: 'Explaining Evidence' },
          { value: 'hooks', displayName: 'Hooks' },
          { value: 'introductions', displayName: 'Introductions' },
          { value: 'logicalAppeal', displayName: 'Logical Appeal (Logos)' },
          {
            value: 'problemSolutionTextStructure',
            displayName: 'Problem/Solution Text Structure',
          },
          { value: 'repetitionAndEmphasis', displayName: 'Repetition and Emphasis' },
          { value: 'sequenceTextStructure', displayName: 'Sequence Text Structure' },
          { value: 'thesisStatements', displayName: 'Thesis Statements' },
          { value: 'transitionWords', displayName: 'Transition Words' },
          {
            value: 'other',
            displayName: 'Other (Add your own focus)',
            displayDivider: true,
          },
        ],
      },
      {
        displayName: 'Other',
        id: 'other',
        prompt: undefined,
      },
    ],
  },
  socialStudies: {
    examplePrompts: fullLessonExamplesSocialStudies,
    displayName: 'Social Studies',

    lessonIntents: [
      {
        displayName: 'History',
        id: 'history',
        hasMoreDetails: false,
      },
      {
        displayName: 'Civics/Government',
        id: 'civicsGovernment',
        hasMoreDetails: false,
      },
      {
        displayName: 'Economics',
        id: 'economics',
        hasMoreDetails: false,
      },
      {
        displayName: 'Psychology',
        id: 'psychology',
        hasMoreDetails: false,
      },
      {
        displayName: 'Other',
        id: 'other',
      },
    ],
  },
  science: {
    examplePrompts: fullLessonExamplesScience,
    displayName: 'Science',
    lessonIntents: [
      {
        displayName: 'Earth Science',
        id: 'earthScience',
        hasMoreDetails: false,
      },
      {
        displayName: 'Space Science',
        id: 'spaceScience',
        hasMoreDetails: false,
      },
      {
        displayName: 'Biology',
        id: 'biology',
        hasMoreDetails: false,
      },
      {
        displayName: 'Chemistry',
        id: 'chemistry',
        hasMoreDetails: false,
      },
      {
        displayName: 'Physics',
        id: 'physics',
        hasMoreDetails: false,
      },
      {
        displayName: 'Engineering',
        id: 'engineering',
        hasMoreDetails: false,
      },
      {
        displayName: 'Other',
        id: 'other',
      },
    ],
  },
  math: {
    examplePrompts: fullLessonExamplesMath,
    displayName: 'Math',
    lessonIntents: [
      {
        displayName: 'Number Sense',
        id: 'numberSense',
        hasMoreDetails: false,
      },
      {
        displayName: 'Measurement',
        id: 'measurement',
        hasMoreDetails: false,
      },
      {
        displayName: 'Algebra',
        id: 'algebra',
        hasMoreDetails: false,
      },
      {
        displayName: 'Geometry',
        id: 'geometry',
        hasMoreDetails: false,
      },
      {
        displayName: 'Statistics',
        id: 'statistics',
        hasMoreDetails: false,
      },
      {
        displayName: 'Trigonometry',
        id: 'trigonometry',
        hasMoreDetails: false,
      },
      {
        displayName: 'Calculus',
        id: 'calculus',
        hasMoreDetails: false,
      },
      {
        displayName: 'Other',
        id: 'other',
      },
    ],
  },
  worldLanguages: {
    examplePrompts: fullLessonExamplesWorldLanguages,
    displayName: 'World Languages',
    lessonIntents: undefined,
  },
  otherSubjects: {
    examplePrompts: undefined,
    displayName: 'Other Subjects',
    lessonIntents: undefined,
  },
};

export const subjectOptionMapperV2: GenerateWithAIType = {
  ela: {
    displayName: 'ELA',
    examplePrompts: fullLessonExamplesELA,
    lessonIntents: [
      {
        displayName: 'Teach Something New',
        id: 'teachSomethingNew',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Reteach & Review',
        id: 'reviewOrReteach',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Practice a Skill',
        id: 'practiceASkill',
        hasMoreDetails: true,
        prompt: 'Students will be able to apply {{focusName}} in',
        dropdownOptions: [
          //Subcategory dropdown reading skills
          {
            value: 'authorsPurpose',
            displayName: "Author's Purpose",
            subCategory: 'readingSkills',
          },
          {
            value: 'centralMessage',
            displayName: 'Central Message',
            subCategory: 'readingSkills',
          },
          {
            value: 'characterization',
            displayName: 'Characterization',
            subCategory: 'readingSkills',
          },
          {
            value: 'comprehensionMonitoring',
            displayName: 'Comprehension Monitoring',
            subCategory: 'readingSkills',
          },
          {
            value: 'figurativeLanguage',
            displayName: 'Figurative Language',
            subCategory: 'readingSkills',
          },
          { value: 'flashback', displayName: 'Flashback', subCategory: 'readingSkills' },
          {
            value: 'foreshadowing',
            displayName: 'Foreshadowing',
            subCategory: 'readingSkills',
          },
          { value: 'imagery', displayName: 'Imagery', subCategory: 'readingSkills' },
          {
            value: 'inferencing',
            displayName: 'Inferencing',
            subCategory: 'readingSkills',
          },
          { value: 'irony', displayName: 'Irony', subCategory: 'readingSkills' },
          {
            value: 'literaryElements',
            displayName: 'Literary Elements',
            subCategory: 'readingSkills',
          },
          { value: 'mainIdea', displayName: 'Main Idea', subCategory: 'readingSkills' },
          {
            value: 'makingConnections',
            displayName: 'Making Connections',
            subCategory: 'readingSkills',
          },
          { value: 'metaphors', displayName: 'Metaphors', subCategory: 'readingSkills' },
          { value: 'mood', displayName: 'Mood', subCategory: 'readingSkills' },
          { value: 'motifs', displayName: 'Motifs', subCategory: 'readingSkills' },
          {
            value: 'paraphrasing',
            displayName: 'Paraphrasing',
            subCategory: 'readingSkills',
          },
          {
            value: 'plotElements',
            displayName: 'Plot Elements',
            subCategory: 'readingSkills',
          },
          {
            value: 'pointOfView',
            displayName: 'Point of View',
            subCategory: 'readingSkills',
          },
          {
            value: 'predicting',
            displayName: 'Predicting',
            subCategory: 'readingSkills',
          },
          {
            value: 'previewing',
            displayName: 'Previewing',
            subCategory: 'readingSkills',
          },
          {
            value: 'questioning',
            displayName: 'Questioning',
            subCategory: 'readingSkills',
          },
          {
            value: 'rhetoricalDevices',
            displayName: 'Rhetorical Devices',
            subCategory: 'readingSkills',
          },
          { value: 'setting', displayName: 'Setting', subCategory: 'readingSkills' },
          {
            value: 'summarizing',
            displayName: 'Summarizing',
            subCategory: 'readingSkills',
          },
          { value: 'symbolism', displayName: 'Symbolism', subCategory: 'readingSkills' },
          {
            value: 'textStructure',
            displayName: 'Text Structure',
            subCategory: 'readingSkills',
          },
          { value: 'theme', displayName: 'Theme', subCategory: 'readingSkills' },
          { value: 'tone', displayName: 'Tone', subCategory: 'readingSkills' },
          {
            value: 'typesOfNarrators',
            displayName: 'Types of Narrators',
            subCategory: 'readingSkills',
          },
          {
            value: 'visualizing',
            displayName: 'Visualizing',
            subCategory: 'readingSkills',
          },
          {
            value: 'other_readingSkills',
            displayName: 'Other (Add your own focus)',
            subCategory: 'readingSkills',
            displayDivider: true,
          },
          //Subcategory dropdown writing skills
          {
            value: 'actionVerbs',
            displayName: 'Action Verbs',
            subCategory: 'writingSkills',
          },
          {
            value: 'alliteration',
            displayName: 'Alliteration',
            subCategory: 'writingSkills',
          },
          {
            value: 'appositives',
            displayName: 'Appositives',
            subCategory: 'writingSkills',
          },
          {
            value: 'causeEffectTextStructure',
            displayName: 'Cause/Effect Text Structure',
            subCategory: 'writingSkills',
          },
          {
            value: 'characterization',
            displayName: 'Characterization',
            subCategory: 'writingSkills',
          },
          {
            value: 'citingEvidence',
            displayName: 'Citing Evidence from a Text',
            subCategory: 'writingSkills',
          },
          { value: 'claims', displayName: 'Claims', subCategory: 'writingSkills' },
          {
            value: 'compareContrastTextStructure',
            displayName: 'Compare/Contrast Text Structure',
            subCategory: 'writingSkills',
          },
          {
            value: 'conclusions',
            displayName: 'Conclusions',
            subCategory: 'writingSkills',
          },
          {
            value: 'conjunctions',
            displayName: 'Conjunctions',
            subCategory: 'writingSkills',
          },
          {
            value: 'credibilityAndAuthority',
            displayName: 'Credibility and Authority (Ethos)',
            subCategory: 'writingSkills',
          },
          {
            value: 'descriptiveTextStructure',
            displayName: 'Descriptive Text Structure',
            subCategory: 'writingSkills',
          },
          { value: 'dialogue', displayName: 'Dialogue', subCategory: 'writingSkills' },
          {
            value: 'emotionalAppeal',
            displayName: 'Emotional Appeal (Pathos)',
            subCategory: 'writingSkills',
          },
          { value: 'evidence', displayName: 'Evidence', subCategory: 'writingSkills' },
          {
            value: 'explainingEvidence',
            displayName: 'Explaining Evidence',
            subCategory: 'writingSkills',
          },
          {
            value: 'figurativeLanguage',
            displayName: 'Figurative Language',
            subCategory: 'writingSkills',
          },
          {
            value: 'formalAndInformalLanguage',
            displayName: 'Formal and Informal Language',
            subCategory: 'writingSkills',
          },
          { value: 'hooks', displayName: 'Hooks', subCategory: 'writingSkills' },
          { value: 'hyperbole', displayName: 'Hyperbole', subCategory: 'writingSkills' },
          { value: 'imagery', displayName: 'Imagery', subCategory: 'writingSkills' },
          {
            value: 'introductions',
            displayName: 'Introductions',
            subCategory: 'writingSkills',
          },
          {
            value: 'logicalAppeal',
            displayName: 'Logical Appeal (Logos)',
            subCategory: 'writingSkills',
          },
          { value: 'metaphors', displayName: 'Metaphors', subCategory: 'writingSkills' },
          {
            value: 'parallelStructure',
            displayName: 'Parallel Structure',
            subCategory: 'writingSkills',
          },
          {
            value: 'paraphrasing',
            displayName: 'Paraphrasing',
            subCategory: 'writingSkills',
          },
          {
            value: 'personification',
            displayName: 'Personification',
            subCategory: 'writingSkills',
          },
          {
            value: 'prepositions',
            displayName: 'Prepositions',
            subCategory: 'writingSkills',
          },
          {
            value: 'problemSolutionTextStructure',
            displayName: 'Problem/Solution Text Structure',
            subCategory: 'writingSkills',
          },
          {
            value: 'repetitionAndEmphasis',
            displayName: 'Repetition and Emphasis',
            subCategory: 'writingSkills',
          },
          {
            value: 'sequenceTextStructure',
            displayName: 'Sequence Text Structure',
            subCategory: 'writingSkills',
          },
          { value: 'similes', displayName: 'Similes', subCategory: 'writingSkills' },
          {
            value: 'supportingDetails',
            displayName: 'Supporting Details',
            subCategory: 'writingSkills',
          },
          { value: 'symbolism', displayName: 'Symbolism', subCategory: 'writingSkills' },
          {
            value: 'thesisStatements',
            displayName: 'Thesis Statements',
            subCategory: 'writingSkills',
          },
          {
            value: 'topicSentence',
            displayName: 'Topic Sentence',
            subCategory: 'writingSkills',
          },
          {
            value: 'transitionWords',
            displayName: 'Transition Words',
            subCategory: 'writingSkills',
          },
          {
            value: 'other_writingSkills',
            displayName: 'Other (Add your own focus)',
            subCategory: 'writingSkills',
            displayDivider: true,
          },
          //Subcategory dropdown grammar and mechanics
          {
            value: 'activeAndPassiveVoice',
            displayName: 'Active and Passive Voice',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'adjectives',
            displayName: 'Adjectives',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'adverbs',
            displayName: 'Adverbs',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'appositives',
            displayName: 'Appositives',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'capitalization',
            displayName: 'Capitalization',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'colonsAndSemicolons',
            displayName: 'Colons and Semicolons',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'complexSentences',
            displayName: 'Complex Sentences',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'compoundSentences',
            displayName: 'Compound Sentences',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'conjunctions',
            displayName: 'Conjunctions',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'endingPunctuation',
            displayName: 'Ending Punctuation',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'formalAndInformalLanguage',
            displayName: 'Formal and Informal Language',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'fragments',
            displayName: 'Fragments',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'homophones',
            displayName: 'Homophones',
            subCategory: 'grammarAndMechanics',
          },
          { value: 'nouns', displayName: 'Nouns', subCategory: 'grammarAndMechanics' },
          {
            value: 'partsOfSpeech',
            displayName: 'Parts of Speech',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'possessives',
            displayName: 'Possessives',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'prepositions',
            displayName: 'Prepositions',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'pronouns',
            displayName: 'Pronouns',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'quotationMarks',
            displayName: 'Quotation Marks',
            subCategory: 'grammarAndMechanics',
          },
          { value: 'runOns', displayName: 'Run-Ons', subCategory: 'grammarAndMechanics' },
          {
            value: 'sentenceVariety',
            displayName: 'Sentence Variety',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'simpleSentences',
            displayName: 'Simple Sentences',
            subCategory: 'grammarAndMechanics',
          },
          {
            value: 'subjectVerbAgreement',
            displayName: 'Subject-Verb Agreement',
            subCategory: 'grammarAndMechanics',
          },
          { value: 'verbs', displayName: 'Verbs', subCategory: 'grammarAndMechanics' },
          {
            value: 'other_grammarAndMechanics',
            displayName: 'Other (Add your own focus)',
            subCategory: 'grammarAndMechanics',
            displayDivider: true,
          },
        ],
      },
      {
        displayName: 'Novel Study',
        id: 'novelStudy',
        hasMoreDetails: true,
        prompt: undefined,
        dropdownOptions: [
          { value: 'authorsPurpose', displayName: "Author's Purpose" },
          { value: 'characterization', displayName: 'Characterization' },
          { value: 'climax', displayName: 'Climax' },
          { value: 'conflict', displayName: 'Conflict' },
          { value: 'figurativeLanguage', displayName: 'Figurative Language' },
          { value: 'flashback', displayName: 'Flashback' },
          { value: 'foreshadowing', displayName: 'Foreshadowing' },
          { value: 'imagery', displayName: 'Imagery' },
          { value: 'inferencing', displayName: 'Inferencing' },
          { value: 'irony', displayName: 'Irony' },
          { value: 'mainIdeas', displayName: 'Main Ideas' },
          { value: 'mood', displayName: 'Mood' },
          { value: 'plot', displayName: 'Plot' },
          { value: 'pointOfView', displayName: 'Point of View' },
          { value: 'predictions', displayName: 'Predictions' },
          { value: 'resolution', displayName: 'Resolution' },
          { value: 'risingAction', displayName: 'Rising Action' },
          { value: 'setting', displayName: 'Setting' },
          { value: 'symbolism', displayName: 'Symbolism' },
          { value: 'theme', displayName: 'Theme' },
          { value: 'tone', displayName: 'Tone' },
          { value: 'typesOfNarrators', displayName: 'Types of Narrators' },
          {
            value: 'other',
            displayName: 'Other (Add your own focus)',
            displayDivider: true,
          },
        ],
      },
      {
        displayName: 'Read Aloud',
        id: 'readAloud',
        hasMoreDetails: true,
        prompt: undefined,
        dropdownOptions: [
          { value: 'authorsPurpose', displayName: "Author's Purpose" },
          { value: 'characterization', displayName: 'Characterization' },
          { value: 'climax', displayName: 'Climax' },
          { value: 'conflict', displayName: 'Conflict' },
          { value: 'figurativeLanguage', displayName: 'Figurative Language' },
          { value: 'flashback', displayName: 'Flashback' },
          { value: 'foreshadowing', displayName: 'Foreshadowing' },
          { value: 'imagery', displayName: 'Imagery' },
          { value: 'inferencing', displayName: 'Inferencing' },
          { value: 'irony', displayName: 'Irony' },
          { value: 'mainIdeas', displayName: 'Main Ideas' },
          { value: 'mood', displayName: 'Mood' },
          { value: 'plot', displayName: 'Plot' },
          { value: 'pointOfView', displayName: 'Point of View' },
          { value: 'predictions', displayName: 'Predictions' },
          { value: 'resolution', displayName: 'Resolution' },
          { value: 'risingAction', displayName: 'Rising Action' },
          { value: 'setting', displayName: 'Setting' },
          { value: 'symbolism', displayName: 'Symbolism' },
          { value: 'theme', displayName: 'Theme' },
          { value: 'tone', displayName: 'Tone' },
          { value: 'typesOfNarrators', displayName: 'Types of Narrators' },
          {
            value: 'other',
            displayName: 'Other (Add your own focus)',
            displayDivider: true,
          },
        ],
      },
      {
        displayName: 'Close Reading',
        id: 'closeReading',
        prompt: undefined,
        comingSoon: true,
      },
      {
        displayName: 'Quick Writes',
        id: 'quickWrites',
        prompt: undefined,
        comingSoon: false,
      },
      {
        displayName: 'Other',
        id: 'other',
        prompt: undefined,
      },
    ],
  },
  socialStudies: {
    examplePrompts: fullLessonExamplesSocialStudies,
    displayName: 'Social Studies',
    lessonIntents: [
      {
        displayName: 'Teach Something New',
        id: 'teachSomethingNew',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Reteach & Review',
        id: 'reviewOrReteach',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Practice a Skill',
        id: 'practiceASkill',
        hasMoreDetails: true,
        prompt: 'Students will be able to apply their knowledge of {{focusName}}.',
      },
      {
        displayName: 'Other',
        id: 'other',
      },
    ],
  },
  science: {
    examplePrompts: fullLessonExamplesScience,
    displayName: 'Science',
    lessonIntents: [
      {
        displayName: 'Teach Something New',
        id: 'teachSomethingNew',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Reteach & Review',
        id: 'reviewOrReteach',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Practice a Skill',
        id: 'practiceASkill',
        hasMoreDetails: true,
        prompt: 'Students will be able to apply their knowledge of {{focusName}}.',
      },
      {
        displayName: 'Other',
        id: 'other',
      },
    ],
  },
  math: {
    examplePrompts: fullLessonExamplesMath,
    displayName: 'Math',
    lessonIntents: [
      {
        displayName: 'Teach Something New',
        id: 'teachSomethingNew',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Reteach & Review',
        id: 'reviewOrReteach',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Practice a Skill',
        id: 'practiceASkill',
        hasMoreDetails: true,
        prompt: 'Students will be able to apply their knowledge of {{focusName}}.',
      },
      {
        displayName: 'Other',
        id: 'other',
      },
    ],
  },
  worldLanguages: {
    examplePrompts: fullLessonExamplesWorldLanguages,
    displayName: 'World Languages',
    lessonIntents: [
      {
        displayName: 'Teach Something New',
        id: 'teachSomethingNew',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Reteach & Review',
        id: 'reviewOrReteach',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Practice a Skill',
        id: 'practiceASkill',
        hasMoreDetails: true,
        prompt: 'Students will be able to apply their knowledge of {{focusName}}.',
      },
      {
        displayName: 'Other',
        id: 'other',
      },
    ],
  },
  otherSubjects: {
    examplePrompts: undefined,
    displayName: 'Other Subjects',
    lessonIntents: [
      {
        displayName: 'Teach Something New',
        id: 'teachSomethingNew',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Reteach & Review',
        id: 'reviewOrReteach',
        hasMoreDetails: true,
        prompt:
          'By the end of the lesson, students will be able to demonstrate mastery of {{focusName}}.',
      },
      {
        displayName: 'Practice a Skill',
        id: 'practiceASkill',
        hasMoreDetails: true,
        prompt: 'Students will be able to apply their knowledge of {{focusName}}.',
      },
      {
        displayName: 'Other',
        id: 'other',
      },
    ],
  },
};

//function that takes lessonIntent and returns just the unique subcategories
// in the form of a dropdown value and display name if they exist
// otherwise returns undefined
export const getSubcategories = (subject: string, lessonIntent: string) => {
  const subcategories = subjectOptionMapperV2[subject].lessonIntents
    ?.find(lesson => lesson.id === lessonIntent)
    ?.dropdownOptions?.reduce((acc, curr) => {
      if (curr.subCategory && !acc.some(item => item.value === curr.subCategory)) {
        acc.push({
          value: curr.subCategory,
          displayName:
            curr.subCategory === 'readingSkills'
              ? 'Reading Skills'
              : curr.subCategory === 'writingSkills'
              ? 'Writing Skills'
              : curr.subCategory === 'grammarAndMechanics'
              ? 'Grammar and Mechanics'
              : curr.subCategory,
        });
      }
      return acc;
    }, [] as { value: string; displayName: string }[])
    ?.map(item => {
      return {
        value: item.value,
        displayName: item.displayName,
      };
    });
  return subcategories;
};

export const findlessonIntent = ({
  subject,
  lessonIntentId,
  hasNewBlockAccess,
}: {
  subject: string;
  lessonIntentId: string;
  hasNewBlockAccess: boolean;
}) => {
  //Takes lesson type id and returns the lesson type object or underfined
  const subjectOptions = hasNewBlockAccess ? subjectOptionMapperV2 : subjectOptionMapper;

  const lessonIntent = subjectOptions[subject]?.lessonIntents?.find(
    lesson => lesson.id === lessonIntentId,
  );

  return lessonIntent ? lessonIntent : undefined;
};
